import React, { useRef, useEffect, useCallback } from "react";
import { Typography, Box, Button, Alert, Chip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { PageContainer, PageSection, SectionTitle } from "../components/layout";
import HeroSection from "../components/HeroSection";
import SearchTripBar from "../components/SearchTripBar";
import TripList from "../components/TripList";
import SkeletonTripList from "../components/SkeletonTripList";
import FeaturesSection from "../components/FeaturesSection";
import PopularTrips from "../components/PopularTrips";
import PopularRouteLists from "../components/PopularRouteLists";
import RelatedDestinationsStrip from "../components/RelatedDestinationsStrip";
import RulesInfoSection from "../components/RulesInfoSection";
import FAQSection from "../components/FAQSection";
import { searchTrips } from "../api";
import { getCityDisplay } from "../utils/city";
import { useBrand } from "../brand/BrandContext";

const RT_OUTBOUND_KEY = "sv_round_trip_outbound";

const DEFAULT_BLOCKS = [
  { type: "hero", enabled: true, props: {} },
  { type: "search", enabled: true, props: {} },
  { type: "popular_trips", enabled: true, props: {} },
  { type: "popular_routes", enabled: true, props: {} },
  { type: "features", enabled: true, props: {} },
  { type: "rules", enabled: true, props: {} },
  { type: "faq", enabled: true, props: {} },
];

const HomePage = () => {
  const { t, i18n } = useTranslation("common");
  const brand = useBrand();
  const lang = i18n.language?.startsWith("en") ? "en" : "uk";
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const [searchedFromCity, setSearchedFromCity] = React.useState("");
  const [searchedToCity, setSearchedToCity] = React.useState("");
  const [searchedDate, setSearchedDate] = React.useState(dayjs());
  const [searchedReturnDate, setSearchedReturnDate] = React.useState(null);
  const [searchedAdults, setSearchedAdults] = React.useState();
  const [searchedChildren, setSearchedChildren] = React.useState();
  const [searchResults, setSearchResults] = React.useState({
    direct: [],
    transfers: [],
    partners: [],
  });
  const [loadingTrips, setLoadingTrips] = React.useState(false);
  const [loadingPartners, setLoadingPartners] = React.useState(false);
  const [showResults, setShowResults] = React.useState(false);
  const [roundTripStep, setRoundTripStep] = React.useState(null);
  const [outboundSelection, setOutboundSelection] = React.useState(null);

  const isRoundTrip = Boolean(
    searchedReturnDate && dayjs(searchedReturnDate).isValid()
  );
  const searchedDateKey = searchedDate?.format?.("YYYY-MM-DD");
  const searchedReturnDateKey = searchedReturnDate?.format?.("YYYY-MM-DD");

  useEffect(() => {
    if (!showResults) return;
    if (isRoundTrip) {
      setRoundTripStep("outbound");
      setOutboundSelection(null);
      try {
        sessionStorage.removeItem(RT_OUTBOUND_KEY);
      } catch {
        /* ignore */
      }
    } else {
      setRoundTripStep(null);
      setOutboundSelection(null);
    }
  }, [
    searchedFromCity,
    searchedToCity,
    searchedDateKey,
    searchedReturnDateKey,
    isRoundTrip,
    showResults,
  ]);

  const hasAnyResults =
    (searchResults.direct?.length ?? 0) > 0 ||
    (searchResults.transfers?.length ?? 0) > 0 ||
    (searchResults.partners?.length ?? 0) > 0;
  const stillSearching = loadingTrips || loadingPartners;

  const focusSearch = () => {
    searchRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    const firstInput = searchRef.current?.querySelector("input");
    if (firstInput) {
      setTimeout(() => firstInput.focus(), 350);
    }
  };

  const listFromCity =
    roundTripStep === "return" ? searchedToCity : searchedFromCity;
  const listToCity =
    roundTripStep === "return" ? searchedFromCity : searchedToCity;
  const listSearchDate =
    roundTripStep === "return" ? searchedReturnDate : searchedDate;

  const handleSelectTrip = useCallback(
    async (payload) => {
      const { trip, fromCity, toCity, adults, children, type, itinerary } =
        payload || {};

      // One-way: transfer goes to transfer checkout.
      // Round-trip: keep transfer as outbound/return selection (do not skip return step).
      if ((type === "transfer" || itinerary) && (!isRoundTrip || !roundTripStep)) {
        navigate("/checkout", {
          state: {
            type: "transfer",
            itinerary,
            fromCity,
            toCity,
            adults,
            children,
          },
        });
        return;
      }

      if (!isRoundTrip || !roundTripStep) {
        navigate("/checkout", {
          state: { trip, fromCity, toCity, adults, children },
        });
        return;
      }

      const isTransferSel = type === "transfer" || !!itinerary;
      const selection = isTransferSel
        ? { type: "transfer", itinerary, fromCity, toCity, adults, children }
        : { type: "direct", trip, fromCity, toCity, adults, children };

      if (roundTripStep === "outbound") {
        setOutboundSelection(selection);
        try {
          sessionStorage.setItem(
            RT_OUTBOUND_KEY,
            JSON.stringify({
              type: selection.type,
              tripId: trip?.id ?? null,
              fromCity,
              toCity,
              adults,
              children,
            })
          );
        } catch {
          /* ignore */
        }

        setRoundTripStep("return");
        setLoadingTrips(true);
        setLoadingPartners(false);
        setSearchResults({ direct: [], transfers: [], partners: [] });
        window.scrollTo({ top: 0, behavior: "smooth" });

        const returnStr = dayjs(searchedReturnDate).format("YYYY-MM-DD");
        try {
          const local = await searchTrips(
            toCity,
            fromCity,
            returnStr,
            adults,
            children,
            lang
          );
          setSearchResults({
            direct: local?.direct ?? [],
            transfers: local?.transfers ?? [],
            partners: [],
          });
        } catch {
          setSearchResults({ direct: [], transfers: [], partners: [] });
        } finally {
          setLoadingTrips(false);
        }
        return;
      }

      if (!outboundSelection) {
        setRoundTripStep("outbound");
        return;
      }

      navigate("/checkout", {
        state: {
          type: "round_trip",
          outbound: outboundSelection,
          returnTrip: selection,
          adults,
          children,
          fromCity: outboundSelection.fromCity,
          toCity: outboundSelection.toCity,
        },
      });
    },
    [
      isRoundTrip,
      roundTripStep,
      navigate,
      searchedReturnDate,
      outboundSelection,
      lang,
    ]
  );

  const handleChangeOutbound = () => {
    setRoundTripStep("outbound");
    setOutboundSelection(null);
    setLoadingTrips(true);
    const dateStr = dayjs(searchedDate).format("YYYY-MM-DD");
    searchTrips(
      searchedFromCity,
      searchedToCity,
      dateStr,
      searchedAdults,
      searchedChildren,
      lang
    )
      .then((local) => {
        setSearchResults({
          direct: local?.direct ?? [],
          transfers: local?.transfers ?? [],
          partners: [],
        });
      })
      .catch(() => {
        setSearchResults({ direct: [], transfers: [], partners: [] });
      })
      .finally(() => setLoadingTrips(false));
  };

  const blocks = (brand.homepage?.blocks?.length
    ? brand.homepage.blocks
    : DEFAULT_BLOCKS
  ).filter((b) => b && b.type && b.enabled !== false);

  const renderBlock = (block) => {
    const key = block.id || block.type;
    if (block.type === "hero") {
      return (
        <HeroSection
          key={key}
          title={block.props?.title}
          subtitle={block.props?.subtitle}
          imageUrl={block.props?.image_url}
        />
      );
    }
    if (block.type === "search") {
      return (
        <Box key={key} ref={searchRef} id="home-trip-search" sx={{ py: 0.25, px: 0 }}>
          <PageContainer maxWidth>
            <SearchTripBar
              setTrips={setSearchResults}
              setShowResults={setShowResults}
              setLoadingTrips={setLoadingTrips}
              setLoadingPartners={setLoadingPartners}
              loadingTrips={loadingTrips || loadingPartners}
              boxWidth="100%"
              setSearchedFromCity={setSearchedFromCity}
              setSearchedToCity={setSearchedToCity}
              setSearchedDate={setSearchedDate}
              setSearchedReturnDate={setSearchedReturnDate}
              setSearchedAdults={setSearchedAdults}
              setSearchedChildren={setSearchedChildren}
            />
          </PageContainer>
        </Box>
      );
    }
    if (showResults) {
      return null;
    }
    if (block.type === "popular_trips") {
      return (
        <PageContainer key={key} sx={{ py: 0 }}>
          <PageSection spacing={0} sx={{ mb: 1 }}>
            <PopularTrips />
          </PageSection>
        </PageContainer>
      );
    }
    if (block.type === "popular_routes") {
      return (
        <Box
          key={key}
          sx={{
            width: "100%",
            bgcolor: "grey.50",
            borderTop: "1px solid",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <PageContainer sx={{ py: 0 }}>
            <PageSection spacing={0} sx={{ mb: 0 }}>
              <PopularRouteLists />
            </PageSection>
          </PageContainer>
        </Box>
      );
    }
    if (block.type === "features") {
      return (
        <PageContainer key={key} sx={{ py: 0, mt: 2 }}>
          <PageSection spacing={1} sx={{ mb: 2 }}>
            <FeaturesSection />
          </PageSection>
        </PageContainer>
      );
    }
    if (block.type === "rules") {
      return (
        <PageContainer key={key} sx={{ py: 0 }}>
          <PageSection spacing={1} sx={{ mb: 2 }}>
            <RulesInfoSection />
          </PageSection>
        </PageContainer>
      );
    }
    if (block.type === "faq") {
      return (
        <PageContainer key={key} sx={{ py: 0 }}>
          <PageSection spacing={1} sx={{ mb: 2 }}>
            <FAQSection />
          </PageSection>
        </PageContainer>
      );
    }
    return null;
  };

  return (
    <>
      {blocks.map(renderBlock)}

      {showResults && (
        <PageContainer sx={{ py: 3 }}>
          <PageSection card spacing={3}>
            <SectionTitle
              title={
                roundTripStep === "return"
                  ? t("roundTrip.returnResults")
                  : t("home.searchResults")
              }
              variant="h5"
              align="left"
            />

            {isRoundTrip && roundTripStep === "outbound" && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t("roundTrip.pickOutbound")}
              </Alert>
            )}

            {isRoundTrip && roundTripStep === "return" && (
              <Box sx={{ mb: 2 }}>
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  {t("roundTrip.pickReturn")}
                </Alert>
                {outboundSelection && (
                  <Chip
                    label={t("roundTrip.outboundSelected", {
                      from: getCityDisplay(
                        { name: outboundSelection.fromCity },
                        lang
                      ),
                      to: getCityDisplay(
                        { name: outboundSelection.toCity },
                        lang
                      ),
                      date: dayjs(
                        outboundSelection.trip?.departure_date ||
                          outboundSelection.itinerary?.legs?.[0]?.trip
                            ?.departure_date
                      ).format("DD.MM.YYYY"),
                    })}
                    onDelete={handleChangeOutbound}
                    color="primary"
                    variant="outlined"
                    sx={{ maxWidth: "100%" }}
                  />
                )}
              </Box>
            )}

            {loadingTrips ? (
              <SkeletonTripList width="90%" />
            ) : hasAnyResults || loadingPartners ? (
              <TripList
                directTrips={searchResults.direct ?? []}
                transferItineraries={searchResults.transfers ?? []}
                partnerTrips={searchResults.partners ?? []}
                loadingPartners={loadingPartners}
                fromCity={listFromCity}
                toCity={listToCity}
                searchDate={listSearchDate || searchedDate}
                adults={searchedAdults}
                children={searchedChildren}
                onSelectTrip={isRoundTrip ? handleSelectTrip : undefined}
                hideTransfers={false}
                hidePartners={isRoundTrip}
              />
            ) : !stillSearching ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.secondary" paragraph>
                  {t("home.noTripsFound")}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={
                    roundTripStep === "return"
                      ? handleChangeOutbound
                      : focusSearch
                  }
                >
                  {roundTripStep === "return"
                    ? t("roundTrip.changeOutbound")
                    : t("home.changeSearchParams")}
                </Button>
              </Box>
            ) : null}
          </PageSection>

          <PageSection spacing={1} sx={{ mt: 2, mb: 1 }}>
            <RelatedDestinationsStrip excludeToCityName={searchedToCity} />
          </PageSection>

          <PageSection spacing={1} sx={{ mb: 2 }}>
            <FAQSection />
          </PageSection>
        </PageContainer>
      )}
    </>
  );
};

export default HomePage;
